import { IStyle, IContextualMenuItem } from '@fluentui/react';
import { NavBarData } from '@suiteux/suiteux-shell-applauncher';
import { ReactElement } from 'react';
import { ITenantProps } from './TenantList/ITenantProps';

export interface IHeaderActionProps {
  iconName: string;
  title: string;
  render?: (props: IHeaderActionProps) => ReactElement;
  onClick?: (props: IHeaderActionProps) => void;
}

export interface IAppItem {
  /**
   * Unique id to identify the item
   */
  key: string;
  /**
   * Text of the app item.
   */
  text: string;
  /**
   * Navigate to this URL when the app item is clicked.
   */
  href: string;

  /**
   * Target window when using `href`.
   */
  target: '_self' | '_blank';

  /**
   * Whether or not this app item is currently app.
   * @defaultvalue false
   */
  selected?: boolean;
  /**
   * Callback for when the menu item is invoked. If `ev.preventDefault()` is called in `onClick`,
   * the click will not close the menu.
   *
   * Only for ContextualMenu items, returning true will dismiss the menu even if `ev.preventDefault()`
   * was called (does not apply for button or CommandBar sub-menu items).
   */
  onClick?: (
    ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    item?: IContextualMenuItem
  ) => boolean | void;
}

export interface IConfiguration {
  /**
   * MSGraph base url
   */
  msGraphBaseUrl: string;
  /**
   * function to get token base on base url
   */
  getToken: (baseUrl: string) => Promise<string>;
}

export interface ISuiteHeaderProps {
  /**
   * Information of current signed in user
   */
  readonly userData: IUserData;
  /**
   * First party app id
   */
  readonly appId: string;
  /**
   * Used by app launcher and MeControl for localization.
   * Examples: 'en-us, zh-cn'
   */
  readonly culture: string;
  /**
   * Contains all the strings that need to be localized in the header
   */
  readonly localizationMap: ILocalizationMap;
  /**
   * Theme
   */
  readonly theme?: IHeaderTheme;
  /**
   * Used by app launcher/waffle to show all the apps
   */
  readonly navbarData: NavBarData;
  /**
   * The app launcher we use is part of the Office suite header.
   * This property uniquely identify partner app that onboard to the Office suite header.
   */
  readonly workloadId: string;
  /**
   * App name to display on the header, such as My Apps, My Account
   */
  readonly appName: string | ReactElement;
  /**
   * App name Title on the header in case appName is JSX, such as My Apps, My Account
   */
  readonly appNameLabel?: string;
  /**
   * When set to true, the header will use the fallback app launcher/waffle.
   */
  readonly isOfficeAppLauncherDisabled?: boolean;
  /**
   * Image URL of tenant branding
   */
  readonly imageUrl?: string;
  /**
   * Contains all the apps that the user has access to
   */
  readonly enabledApps: IAppItem[];
  /**
   * All the tenants that the user belongs to, including the home tenant.
   * This will be displayed as a list in the tenant picker panel
   */
  readonly organizations: ILoadable<IOrganization[]>;
  /**
   * If there is error calling get organizations API
   */
  readonly hasError: boolean;
  /**
   * Link to the help doc of the app
   * This will be displayed in the help panel
   */
  readonly helpLink: string;
  /**
   * Link to the AAD My Account page, which is different per environment (PPE, PROD, national clouds, etc)
   * This will be displayed in the fallback MeControl
   */
  readonly viewAccountLink: string;
  /**
   * Base URL of Evo page
   * This need to be set when using the universal MeControl
   */
  readonly evoBase: string;
  /**
   * Is Universal MeControl enabled
   */
  readonly isUniversalMeControlEnabled: boolean;
  /**
   * Is MSAL.js enabled, this will determine the behavior of MeControl, default to false.
   * For details, see https://mecontroltestpartner.azurewebsites.net/PartnerDocs/articles/guides/msalJs-authProvider.html
   */
  readonly isMsalJsEnabled?: boolean;
  /**
   * Callback when the header finishes loading
   */
  readonly onRenderComplete: () => void;
  /**
   * Callback when the header button is clicked
   */
  readonly onHeaderButtonClicked: (buttonId: string) => void;
  /**
   * Callback when click the app branding
   */
  readonly onClickAppBranding: () => void;
  /**
   * The consumer app pass its own search box component.
   * Header's built-in styling will be added
   */
  readonly searchBox?: () => JSX.Element;
  /**
   * Callback to leave the new experience and go to the old one (i.e IAMUX portal)
   * This is required for both universal MeControl and fallback MeControl
   */
  readonly onLeavePreview?: () => void;
  /**
   * Callback to trigger the tenant picker header button to open the organization panel
   * This is required for universal MeControl
   */
  readonly openOrganizationPanel?: (id: FlexPanelCollections, shouldOpen: boolean) => void;
  /**
   * onClick callback for each tenant of the organizations array, which will log into this tenant
   */
  readonly changeTenant: (targetTenant: ITenantProps, currentTenant?: IOrganization) => void;
  /**
   * Callback to return the control that manages the tenant/organization
   */
  readonly manageTenantControl: () => ReactElement;
  /**
   * Callback to switch to another account
   */
  readonly onSwitch?: () => void;
  /**
   * Callback to switch to another account from the remembered account list
   */
  readonly onSwitchTo?: (upn: string) => void;
  /**
   * Callback to sign out the current user
   */
  readonly onSignOut: () => void;
  /**
   * Add action (icon) buttons.
   */
  readonly extraActions?: IHeaderActionProps[];
}

export interface ISuiteHeaderStyles {
  root?: IStyle;
  leftRegion?: IStyle;
  centerRegion?: IStyle;
  rightRegion?: IStyle;
  leftButtonGroup?: IStyle;
  divider?: IStyle;
  appBranding?: IStyle;
  centerSearchGroup?: IStyle;
  appSwitcher?: IStyle;
  rightButtonGroup?: IStyle;
  buttonGroupExpanded?: IStyle;
  buttonGroupCollapsed?: IStyle;
  waffle?: IStyle;
}

export interface IUserData {
  /**
   * The id of the tenant that the user logs in
   */
  readonly tenantId: string;
  /**
   * Tenant display name, this will be shown on the top left corner of MeControl
   */
  readonly tenantDisplayName?: string;
  /**
   * User principal name, usually the email address
   */
  readonly upn: string;
  /**
   * User display name
   */
  readonly name: string;
  /**
   * User's profile image URL
   */
  readonly imageUrl?: string;
  /**
   * User's raw claims
   */
  readonly rawClaims: {
    altsecid?: unknown;
    puid?: unknown;
    [key: string]: unknown;
  };
  /**
   * User's home tenantId
   */
  readonly homeTenantId: string;
}

export interface ILocalizationMap {
  readonly apps: string;
  readonly waffleNotAvailableHeader: string;
  readonly waffleNotAvailableDescription: string;
  readonly tenantBrandingImageAlt: string;
  readonly organizations: string;
  readonly switchOrgIntroText: string;
  readonly loadingOrganizations: string;
  readonly organizationsEmpty: string;
  readonly currentOrganizationTitle: string;
  readonly otherOrganizationsTitle: string;
  readonly finishLoading: string;
  readonly organizationError: string;
  readonly manageOrganizations: string;
  readonly helpPanelHeaderText: string;
  readonly helpPanelTermsOfUseLink: string;
  readonly helpPanelPrivacyLink: string;
  readonly accessibilityLink: string;
  readonly viewApps: string;
  readonly moreOptions: string;
  readonly accountManager: string;
  readonly switchOrganization: string;
  readonly leaveNewExp: string;
  readonly closePanelAriaLabel: string;
}

export interface IHeaderTheme {
  readonly backgroundColor: string;
  readonly foregroundColor: string;
}

/**
 * All the built-in flex panel ids
 */
export enum FlexPanelCollections {
  appLauncher = 'appLauncher',
  organizations = 'organizations',
  help = 'help',
  meControl = 'meControl'
}

interface IPrivacyProfile {
  readonly contactEmail?: string;
  readonly statementUrl?: string;
}
export interface IOrganization {
  readonly homeTenantId?: string;
  readonly displayName: string;
  readonly privacyProfile?: IPrivacyProfile;
  readonly tenantId: string;
  readonly isCurrent: boolean;
  readonly isHome: boolean;
  readonly isNIJoined?: boolean;
  readonly isCollabJoined?: boolean;
}

export interface ILoadable<T> {
  readonly isLoading: boolean;
  readonly value: T;
}
